import React, { useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
} from "@mui/material";
import {
  ScatterChart as RechartsScatterChart,
  Scatter as RechartsScatter,
  XAxis as RechartsXAxis,
  YAxis as RechartsYAxis,
  CartesianGrid as RechartsCartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell as RechartsCell,
} from "recharts";
import dayjs from "dayjs";
import { LineChart, VerticalBarChart } from "../../../../../../shared/components/charts";
import { useMetricsTrends, useMetricsComparison } from "../../../../../../shared/hooks";
import { transformTrendsData, calculateGranularity } from "../../../shared/utils";
import { toComparisonKey } from "../../../../../../shared/utils";
import { brandColors } from "../../../../../../theme/brandColors";
import type { ComparisonBlock } from "../../api/types";

interface ComparisonViewProps {
  block: ComparisonBlock;
  clientId: string | null;
  branding?: { primaryColor?: string; chartColor?: string };
  fill?: boolean;
}

const METRIC_LABELS: Record<string, string> = {
  recovery_score: "Recovery Score",
  hrv: "HRV",
  sleep_score: "Sleep",
  sleep_performance: "Sleep Performance",
  sleep_efficiency: "Sleep Efficiency",
  sleep_duration: "Sleep Duration",
  strain: "Strain",
  resting_heart_rate: "Resting HR",
  respiratory_rate: "Resp. Rate",
  overall_stress: "Overall Stress",
};

const ml = (k: string) => METRIC_LABELS[k] ?? k;

const SEGMENT_LABELS: Record<string, string> = {
  department: "Department",
  age_group: "Age Group",
  gender: "Gender",
  shift_pattern: "Shift Pattern",
};

const SCATTER_PALETTE = [
  brandColors.peak4Blue,
  brandColors.summitGreen,
  brandColors.performOrange,
  brandColors.fuelIndigo,
  brandColors.recoverPink,
  brandColors.focusLilac,
];

export const ComparisonView: React.FC<ComparisonViewProps> = ({
  block,
  clientId,
  fill = false,
}) => {
  const theme = useTheme();
  const colorA = theme.palette.info.main;
  const colorB = theme.palette.success.main;

  const granularity = useMemo(
    () =>
      calculateGranularity([
        block.filters.start_date ? dayjs(block.filters.start_date) : null,
        block.filters.end_date ? dayjs(block.filters.end_date) : null,
      ]),
    [block.filters.start_date, block.filters.end_date],
  );

  const comparisonKeyA = useMemo(
    () => toComparisonKey(block.metricA) ?? block.metricA,
    [block.metricA],
  );
  const comparisonKeyB = useMemo(
    () => toComparisonKey(block.metricB) ?? block.metricB,
    [block.metricB],
  );

  const metricsForQuery = useMemo(
    () => [block.metricA, block.metricB],
    [block.metricA, block.metricB],
  );

  const comparisonMetrics = useMemo(
    () => Array.from(new Set([comparisonKeyA, comparisonKeyB])),
    [comparisonKeyA, comparisonKeyB],
  );

  const baseParams = useMemo(
    () => ({ ...block.filters, client_id: clientId }),
    [block.filters, clientId],
  );

  const trendsQuery = useMetricsTrends({
    ...baseParams,
    metrics: metricsForQuery,
    granularity,
  });

  const comparisonQuery = useMetricsComparison({
    ...baseParams,
    group_by: block.segmentBy,
    metrics: comparisonMetrics,
  });

  const trendData = useMemo(
    () =>
      trendsQuery.data ? transformTrendsData(trendsQuery.data, metricsForQuery) : [],
    [trendsQuery.data, metricsForQuery],
  );

  const groups = useMemo(
    () => comparisonQuery.data?.data?.groups ?? [],
    [comparisonQuery.data],
  );

  const scatterPoints = useMemo(
    () =>
      groups
        .filter(
          (g) =>
            g.values[comparisonKeyA] !== null &&
            g.values[comparisonKeyA] !== undefined &&
            g.values[comparisonKeyB] !== null &&
            g.values[comparisonKeyB] !== undefined,
        )
        .map((g, i) => ({
          x: g.values[comparisonKeyA] as number,
          y: g.values[comparisonKeyB] as number,
          label: g.group_label,
          color: SCATTER_PALETTE[i % SCATTER_PALETTE.length],
        })),
    [groups, comparisonKeyA, comparisonKeyB],
  );

  const segLabel = SEGMENT_LABELS[block.segmentBy] ?? block.segmentBy;

  const barData = useMemo(
    () =>
      groups.map((g) => ({
        [segLabel]: g.group_label,
        [block.metricA]: g.values[comparisonKeyA] ?? 0,
        [block.metricB]: g.values[comparisonKeyB] ?? 0,
      })),
    [groups, block.metricA, block.metricB, comparisonKeyA, comparisonKeyB, segLabel],
  );

  const isLoading =
    block.viewMode === "trend" ? trendsQuery.isLoading : comparisonQuery.isLoading;
  const isError =
    block.viewMode === "trend" ? trendsQuery.isError : comparisonQuery.isError;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error">Couldn't load comparison data.</Alert>;
  }

  return (
    <Box sx={{ width: "100%" }}>
      {block.title && (
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          {block.title}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        {ml(block.metricA)} vs {ml(block.metricB)} · {segLabel}
      </Typography>

      {block.viewMode === "trend" && (
        <>
          {trendData.length === 0 ? (
            <Alert severity="info">No trend data for this period.</Alert>
          ) : (
            <LineChart
              data={trendData}
              xAxisKey="label"
              series={[
                { dataKey: block.metricA, label: ml(block.metricA), color: colorA },
                { dataKey: block.metricB, label: ml(block.metricB), color: colorB },
              ]}
              height={fill ? 300 : 240}
            />
          )}
        </>
      )}

      {block.viewMode === "segmented" && (
        <>
          {barData.length === 0 ? (
            <Alert severity="info">No segment data for this period.</Alert>
          ) : (
            <VerticalBarChart
              data={barData}
              xAxisKey={segLabel}
              series={[
                { dataKey: block.metricA, label: ml(block.metricA), color: colorA },
                { dataKey: block.metricB, label: ml(block.metricB), color: colorB },
              ]}
              height={fill ? 300 : 240}
            />
          )}
        </>
      )}

      {block.viewMode === "scatter" && (
        <>
          {scatterPoints.length === 0 ? (
            <Alert severity="info">No paired data for this period.</Alert>
          ) : (
            <ResponsiveContainer width="100%" height={fill ? 320 : 260}>
              <RechartsScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 20 }}>
                <RechartsCartesianGrid strokeDasharray="3 3" />
                <RechartsXAxis
                  type="number"
                  dataKey="x"
                  name={ml(block.metricA)}
                  label={{ value: ml(block.metricA), position: "insideBottom", offset: -15 }}
                />
                <RechartsYAxis
                  type="number"
                  dataKey="y"
                  name={ml(block.metricB)}
                  label={{ value: ml(block.metricB), angle: -90, position: "insideLeft" }}
                />
                <RechartsTooltip
                  content={({ payload }) => {
                    if (!payload?.length) return null;
                    const d = payload[0]?.payload as (typeof scatterPoints)[0];
                    return (
                      <Box
                        sx={{
                          bgcolor: "background.paper",
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          p: 1,
                          fontSize: 12,
                        }}
                      >
                        <Typography variant="caption" fontWeight={700}>
                          {d.label}
                        </Typography>
                        <br />
                        <Typography variant="caption">
                          {ml(block.metricA)}: {d.x}
                        </Typography>
                        <br />
                        <Typography variant="caption">
                          {ml(block.metricB)}: {d.y}
                        </Typography>
                      </Box>
                    );
                  }}
                />
                <RechartsScatter data={scatterPoints}>
                  {scatterPoints.map((_p, i) => (
                    <RechartsCell key={i} fill={scatterPoints[i].color} />
                  ))}
                </RechartsScatter>
              </RechartsScatterChart>
            </ResponsiveContainer>
          )}
        </>
      )}
    </Box>
  );
};
